import { GlossaryItem } from "../../../models/reader";

let tooltipIdCounter = 0;

const SKIP_SELECTOR = "a, .translation, [role='tooltip']";

type Match = {
  node: Text;
  start: number;
  wordStart: number;
  wordEnd: number;
  end: number;
};

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function wordRegex(word: string): RegExp {
  return new RegExp(
    `(?<![\\p{L}\\p{N}])${escapeRegex(word)}(?![\\p{L}\\p{N}])`,
    "iu",
  );
}

function findMatch(root: Element, regex: RegExp): Match | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.parentElement?.closest(SKIP_SELECTOR)) {
        return NodeFilter.FILTER_REJECT;
      }
      return regex.test(node.nodeValue ?? "")
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    },
  });

  const node = walker.nextNode() as Text | null;
  if (!node) return null;

  const text = node.nodeValue ?? "";
  const m = regex.exec(text);
  if (!m) return null;

  const wordStart = m.index;
  const wordEnd = wordStart + m[0].length;

  const before = text.slice(0, wordStart).match(/[^\s-]*$/)?.[0] ?? "";
  const after = text.slice(wordEnd).match(/^(?:[^\s-]|[\s-](?=!))*/)?.[0] ?? "";

  return {
    node,
    start: wordStart - before.length,
    wordStart,
    wordEnd,
    end: wordEnd + after.length,
  };
}

function buildTranslationSpan(
  prefix: string,
  word: string,
  suffix: string,
  translation: string,
): HTMLSpanElement {
  const tooltipId = `tt-${tooltipIdCounter++}`;

  const wrapper = document.createElement("span");
  wrapper.className = "translation glossary-applied";

  const highlight = document.createElement("span");
  highlight.className = "highlight";
  highlight.tabIndex = 0;
  highlight.setAttribute("data-tooltip", translation); // escaped automatically
  highlight.setAttribute("aria-describedby", tooltipId);
  highlight.textContent = word;

  const tooltip = document.createElement("span");
  tooltip.id = tooltipId;
  tooltip.setAttribute("role", "tooltip");
  tooltip.hidden = true;
  tooltip.textContent = translation;

  if (prefix) wrapper.append(prefix);
  wrapper.append(highlight, tooltip);
  if (suffix) wrapper.append(suffix);

  return wrapper;
}

function applyTranslation(
  root: Element,
  item: GlossaryItem,
  regex: RegExp,
): boolean {
  const match = findMatch(root, regex);
  if (!match) return false;

  const { node, start, wordStart, wordEnd, end } = match;
  const text = node.nodeValue ?? "";

  // Split the text node into: before | chunk | after
  const chunk = node.splitText(start);
  chunk.splitText(end - start);

  chunk.replaceWith(
    buildTranslationSpan(
      text.slice(start, wordStart),
      text.slice(wordStart, wordEnd),
      text.slice(wordEnd, end),
      item.translation.trim(),
    ),
  );
  return true;
}

async function runGlossary(glossary: GlossaryItem[]) {
  if (!glossary) return;

  // Avoid re-run issues
  if (document.querySelector(".glossary-applied")) return;

  const sorted = [...glossary].sort(
    (a, b) => b.word.trim().length - a.word.trim().length,
  );

  const elements = Array.from(document.querySelectorAll("p, h2"));
  const shown_translations = new Set<string>();

  for (const item of sorted) {
    const spanish = item.word.trim();
    if (!spanish || shown_translations.has(spanish)) continue;

    const regex = wordRegex(spanish);
    for (const element of elements) {
      if (applyTranslation(element, item, regex)) {
        shown_translations.add(spanish);
        break;
      }
    }
  }

  return true;
}

export default runGlossary;
